import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CircularSlider from '../CircularSlider';
import { winnersApi } from '../../services/api';
import { mapYearIdsToYears } from '../../utils/yearMapper';

interface YearData {
  id: string;
  year: string;
  winners: number;
  categories: number;
}

const ArchivesSection = () => {
  const [years, setYears] = useState<YearData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchYearsData = async () => {
      try {
        setLoading(true);
        
        // Get available years from API (returns database IDs: 1, 2, 3, 4)
        const dbYears = await winnersApi.getYears();
        
        // Convert to actual years (2021, 2022, 2023, 2024)
        const actualYears = mapYearIdsToYears(dbYears);
        
        // Fetch winners for each year and count them
        const yearsData = await Promise.all(
          actualYears.map(async (year, index) => {
            try {
              const dbYearId = dbYears[index];
              const winnersData = await winnersApi.getByYear(dbYearId);
              
              // Count total winners and categories
              const categories = Object.keys(winnersData).length;
              const winners = Object.values(winnersData).reduce(
                (total: number, categoryWinners: unknown) => {
                  if (Array.isArray(categoryWinners)) {
                    return total + categoryWinners.length;
                  }
                  return total;
                },
                0
              );
              
              return {
                id: dbYearId,
                year: year.toString(),
                winners,
                categories
              };
            } catch (error) {
              console.warn(`Failed to fetch data for year ${year}:`, error);
              return {
                id: dbYears[index],
                year: year.toString(),
                winners: 0,
                categories: 0
              };
            }
          })
        );
        
        // Sort by year descending (most recent first)
        yearsData.sort((a, b) => parseInt(b.year) - parseInt(a.year));
        
        setYears(yearsData);
      } catch (error) {
        console.error('Failed to fetch years data:', error);
        // Fallback to empty array
        setYears([]);
      } finally {
        setLoading(false);
      }
    };

    fetchYearsData();
  }, []);

  return (
    <section className="py-12 md:py-24 bg-white" style={{ backgroundColor: '#f9fafb', padding: '3rem 0' }}>
      <div className="container mx-auto px-4" style={{ maxWidth: '1400px' }}>
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-20" style={{ marginBottom: '3rem' }}>
          <span className="text-orange-500 font-semibold text-xs md:text-sm uppercase tracking-wider mb-3 md:mb-4 block" style={{ color: '#ffba17', letterSpacing: '0.1em', fontSize: '0.75rem' }}>
            Our Journey
          </span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 md:mb-6" style={{ fontSize: 'clamp(1.875rem, 4vw, 3.5rem)', fontWeight: '700', color: '#111827', marginBottom: '1rem' }}>
            Previous Winners
          </h2>
          <p className="text-gray-600 text-base md:text-xl max-w-2xl mx-auto" style={{ color: '#6b7280', fontSize: 'clamp(1rem, 2vw, 1.125rem)', maxWidth: '42rem', margin: '0 auto' }}>
            Celebrating excellence and innovation across Tanzania's digital landscape
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        ) : years.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">No winners data available at the moment.</p>
          </div>
        ) : (
          <>
        {/* Years Grid - Desktop: Centered with padding, Mobile: Horizontal scroll */}
        <div className="hidden md:flex justify-center px-16" style={{ paddingLeft: '4rem', paddingRight: '4rem' }}>
          <div className="grid grid-cols-4 gap-6 max-w-5xl" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem', maxWidth: '80rem' }}>
          {years.map((yearData) => (
            <Link 
              to={`/winners/${yearData.year}`} 
              key={yearData.id} 
              className="group relative"
              style={{ textDecoration: 'none' }}
            >
              <div 
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
                style={{ 
                  backgroundColor: 'white', 
                  borderRadius: '1rem', 
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  transition: 'all 0.5s ease',
                  overflow: 'hidden'
                }}
              >
                {/* Year Display - Medium size */}
                <div 
                  className="relative bg-gradient-to-br from-blue-900 to-blue-800 p-10 text-center"
                  style={{ 
                    background: 'linear-gradient(135deg, #0f1b3c 0%, #0f1b3c 100%)',
                    padding: '2.5rem',
                    textAlign: 'center'
                  }}
                >
                  <div className="relative z-10">
                    <h3 
                      className="text-6xl font-bold text-white mb-2"
                      style={{ 
                        fontSize: '3.75rem', 
                        fontWeight: '700', 
                        color: 'white',
                        marginBottom: '0.5rem',
                        lineHeight: '1'
                      }}
                    >
                      {yearData.year}
                    </h3>
                    <div className="w-14 h-1 bg-orange-500 mx-auto mt-4" style={{ width: '3.5rem', height: '0.25rem', backgroundColor: '#ffba17', margin: '1rem auto 0' }}></div>
                  </div>
                  
                  {/* Decorative Circle */}
                  <div 
                    className="absolute top-0 right-0 w-32 h-32 bg-white opacity-5 rounded-full transform translate-x-8 -translate-y-8"
                    style={{ 
                      position: 'absolute', 
                      top: '0', 
                      right: '0', 
                      width: '8rem', 
                      height: '8rem', 
                      backgroundColor: 'white', 
                      opacity: '0.05', 
                      borderRadius: '50%',
                      transform: 'translate(2rem, -2rem)'
                    }}
                  ></div>
                </div>

                {/* Stats - Medium size */}
                <div className="p-7" style={{ padding: '1.75rem' }}>
                  <div className="space-y-4" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div className="flex items-center justify-between" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span className="text-gray-600 font-medium" style={{ color: '#6b7280', fontWeight: '500', fontSize: '0.875rem' }}>Winners</span>
                      <span className="text-2xl font-bold text-gray-900" style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827' }}>{yearData.winners}</span>
                    </div>
                    <div className="flex items-center justify-between" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span className="text-gray-600 font-medium" style={{ color: '#6b7280', fontWeight: '500', fontSize: '0.875rem' }}>Categories</span>
                      <span className="text-2xl font-bold text-gray-900" style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827' }}>{yearData.categories}</span>
                    </div>
                  </div>

                  {/* View Button */}
                  <div className="mt-7 pt-5 border-t border-gray-200" style={{ marginTop: '1.75rem', paddingTop: '1.25rem', borderTop: '1px solid #e5e7eb' }}>
                    <div 
                      className="flex items-center justify-center text-orange-500 font-semibold group-hover:text-orange-600 transition-colors"
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        color: '#ffba17', 
                        fontWeight: '600',
                        transition: 'color 0.3s ease',
                        fontSize: '0.875rem'
                      }}
                    >
                      <span style={{ marginRight: '0.5rem' }}>View Winners</span>
                      <i className="fa-solid fa-arrow-right transform group-hover:translate-x-1 transition-transform" style={{ transition: 'transform 0.3s ease' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
          </div>
        </div>

        {/* Mobile Horizontal Scroll */}
        <div className="md:hidden px-4">
          <div 
            className="flex gap-3 overflow-x-auto pb-4"
            style={{
              display: 'flex',
              gap: '0.75rem',
              overflowX: 'auto',
              paddingBottom: '1rem',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            {years.map((yearData) => (
              <Link 
                to={`/winners/${yearData.year}`} 
                key={yearData.id} 
                className="group relative flex-shrink-0"
                style={{ textDecoration: 'none', flexShrink: 0 }}
              >
                <div 
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300"
                  style={{ 
                    backgroundColor: 'white', 
                    borderRadius: '0.75rem', 
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    overflow: 'hidden',
                    width: '200px',
                    minWidth: '200px'
                  }}
                >
                  {/* Year Display - Small size */}
                  <div 
                    className="relative bg-gradient-to-br from-blue-900 to-blue-800 p-4 text-center"
                    style={{ 
                      background: 'linear-gradient(135deg, #0f1b3c 0%, #0f1b3c 100%)',
                      padding: '1rem',
                      textAlign: 'center'
                    }}
                  >
                    <div className="relative z-10">
                      <h3 
                        className="text-2xl font-bold text-white mb-1"
                        style={{ 
                          fontSize: '1.5rem', 
                          fontWeight: '700', 
                          color: 'white',
                          marginBottom: '0.25rem',
                          lineHeight: '1'
                        }}
                      >
                        {yearData.year}
                      </h3>
                      <div className="w-8 h-0.5 bg-orange-500 mx-auto mt-2" style={{ width: '2rem', height: '0.125rem', backgroundColor: '#ffba17', margin: '0.5rem auto 0' }}></div>
                    </div>
                  </div>

                  {/* Stats - Small size */}
                  <div className="p-3" style={{ padding: '0.75rem' }}>
                    <div className="space-y-2" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div className="flex items-center justify-between" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span className="text-gray-600 font-medium text-xs" style={{ color: '#6b7280', fontWeight: '500', fontSize: '0.75rem' }}>Winners</span>
                        <span className="text-lg font-bold text-gray-900" style={{ fontSize: '1.125rem', fontWeight: '700', color: '#111827' }}>{yearData.winners}</span>
                      </div>
                      <div className="flex items-center justify-between" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span className="text-gray-600 font-medium text-xs" style={{ color: '#6b7280', fontWeight: '500', fontSize: '0.75rem' }}>Categories</span>
                        <span className="text-lg font-bold text-gray-900" style={{ fontSize: '1.125rem', fontWeight: '700', color: '#111827' }}>{yearData.categories}</span>
                      </div>
                    </div>

                    {/* View Button */}
                    <div className="mt-3 pt-2 border-t border-gray-200" style={{ marginTop: '0.75rem', paddingTop: '0.5rem', borderTop: '1px solid #e5e7eb' }}>
                      <div 
                        className="flex items-center justify-center text-orange-500 font-semibold text-xs group-hover:text-orange-600 transition-colors"
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          color: '#ffba17', 
                          fontWeight: '600',
                          transition: 'color 0.3s ease',
                          fontSize: '0.75rem'
                        }}
                      >
                        <span style={{ marginRight: '0.25rem' }}>View</span>
                        <i className="fa-solid fa-arrow-right transform group-hover:translate-x-1 transition-transform" style={{ transition: 'transform 0.3s ease' }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12 md:mt-16" style={{ textAlign: 'center', marginTop: '3rem' }}>
          <p className="text-gray-600 text-base md:text-lg mb-4 md:mb-6" style={{ color: '#6b7280', fontSize: '1rem', marginBottom: '1rem' }}>
            Want to be part of our next success story?
          </p>
          <Link 
            to="/vote/" 
            className="inline-flex items-center px-6 py-3 md:px-8 md:py-4 bg-orange-500 text-white rounded-full font-semibold text-base md:text-lg hover:bg-orange-600 transition-colors duration-300 group"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.75rem 1.5rem',
              backgroundColor: '#ffba17',
              color: 'white',
              borderRadius: '9999px',
              fontWeight: '600',
              fontSize: '1rem',
              textDecoration: 'none',
              transition: 'background-color 0.3s ease'
            }}
          >
            Nominate Now
            <i className="fa-solid fa-arrow-right ml-2 md:ml-3 group-hover:translate-x-1 transition-transform duration-300" style={{ marginLeft: '0.5rem', transition: 'transform 0.3s ease' }}></i>
          </Link>
        </div>

        {/* Circular Slider - Only slides when images exceed viewport */}
        <div className="mt-8 md:mt-12" style={{ marginTop: '2rem' }}>
          <CircularSlider
            images={[
              { src: "/assets/webpage/1.png", alt: "TDA Event 1", id: 1 },
              { src: "/assets/webpage/2.png", alt: "TDA Event 2", id: 2 },
              { src: "/assets/webpage/TDA2024_182.jpg", alt: "TDA Event 3", id: 3 },
              { src: "/assets/webpage/8.jpg", alt: "TDA Event 4", id: 4 },
              { src: "/assets/webpage/9.png", alt: "TDA Event 5", id: 5 },
              { src: "/assets/webpage/10.jpeg", alt: "TDA Event 6", id: 6 },
              { src: "/assets/webpage/05.png", alt: "TDA Event 7", id: 7 },
              { src: "/assets/webpage/06.png", alt: "TDA Event 8", id: 8 },
              { src: "/assets/webpage/07.png", alt: "TDA Event 9", id: 9 },
              { src: "/assets/webpage/12.jpg", alt: "TDA Event 10", id: 10 }
            ]}
            imageSize="5rem"
            gap="1rem"
            animationDuration={20}
            responsive={{
              mobile: { imageSize: '4rem', gap: '0.75rem' },
              tablet: { imageSize: '4.5rem', gap: '0.875rem' },
              desktop: { imageSize: '5rem', gap: '1rem' }
            }}
          />
        </div>
        </>
        )}
      </div>
    </section>
  );
};

export default ArchivesSection;
